import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { cloudConfigured, supabase } from "../lib/supabase";
import type { Profile, WarehouseRow } from "../types";

type AuthContextValue = {
  cloudConfigured: boolean;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  profileError: string;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
};

const demoProfile: Profile = {
  id: "local-preview",
  email: null,
  full_name: "Entwicklung",
  role: "admin",
  warehouse_id: null,
  is_active: true,
  warehouse: null
};

const AuthContext = createContext<AuthContextValue | null>(null);

function normalizeProfile(row: Record<string, unknown>): Profile {
  const relation = row.warehouse;
  const warehouse = (Array.isArray(relation) ? relation[0] : relation) as Pick<WarehouseRow, "id" | "code" | "name"> | null | undefined;
  return {
    id: String(row.id),
    email: row.email ? String(row.email) : null,
    full_name: row.full_name ? String(row.full_name) : null,
    role: row.role as Profile["role"],
    warehouse_id: row.warehouse_id ? String(row.warehouse_id) : null,
    is_active: Boolean(row.is_active),
    warehouse: warehouse ?? null
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(cloudConfigured ? null : demoProfile);
  const [loading, setLoading] = useState(cloudConfigured);
  const [profileError, setProfileError] = useState("");
  const sessionUserId = session?.user.id;

  const refreshProfile = useCallback(async () => {
    if (!supabase || !sessionUserId) return;
    const { data, error } = await supabase
      .from("profiles")
      .select("id,email,full_name,role,warehouse_id,is_active,warehouse:warehouses(id,code,name)")
      .eq("id", sessionUserId)
      .maybeSingle();

    if (error || !data) {
      setProfile(null);
      setProfileError("Das Benutzerprofil konnte nicht geladen werden.");
      return;
    }
    setProfile(normalizeProfile(data as Record<string, unknown>));
    setProfileError("");
  }, [sessionUserId]);

  useEffect(() => {
    if (!supabase) return;
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      if (!data.session) setLoading(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      if (!nextSession) {
        setProfile(null);
        setProfileError("");
        setLoading(false);
      }
    });
    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!supabase || !sessionUserId) return;
    let active = true;
    setLoading(true);
    refreshProfile().finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [refreshProfile, sessionUserId]);

  const value = useMemo<AuthContextValue>(() => ({
    cloudConfigured,
    session,
    profile,
    loading,
    profileError,
    refreshProfile,
    signOut: async () => {
      if (supabase) await supabase.auth.signOut();
    }
  }), [session, profile, loading, profileError, refreshProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth muss innerhalb des AuthProvider verwendet werden.");
  return context;
}
